import React, { useState, useEffect, useRef } from 'react';
import { Check, Zap, Crown, Shield, Clock, Users, BarChart3, MessageCircle, Play, ChevronDown, ArrowRight, Star, Lock, RefreshCw, Pause } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KEYFRAMES = `
  @keyframes floatA { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-22px) scale(1.03)} }
  @keyframes floatB { 0%,100%{transform:translateY(-6px) rotate(0deg)} 50%{transform:translateY(10px) rotate(4deg)} }
  @keyframes gradShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
  @keyframes rippleOut { 0%{transform:scale(0);opacity:0.4} 100%{transform:scale(4.5);opacity:0} }
  @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
  @keyframes badgePop { 0%{transform:translateX(-50%) scale(0.7);opacity:0} 70%{transform:translateX(-50%) scale(1.06)} 100%{transform:translateX(-50%) scale(1);opacity:1} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes borderGlow { 0%,100%{box-shadow:0 0 20px rgba(56,254,96,0.15),0 0 0 1px rgba(56,254,96,0.25)} 50%{box-shadow:0 0 40px rgba(56,254,96,0.3),0 0 0 1px rgba(56,254,96,0.5)} }
  @keyframes countUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
`;

function useVisible(thresh = 0.1) {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setV(true), { threshold: thresh });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}

const FadeIn = ({ children, delay = 0, style: extra = {}, className = '' }) => {
  const [ref, vis] = useVisible(0.07);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(28px)',
      transition: `opacity 0.7s ${delay}ms ease, transform 0.7s ${delay}ms ease`,
      ...extra,
    }}>
      {children}
    </div>
  );
};

const PLANS = [
  {
    id: 1,
    name: '30-Day Access',
    price: 99,
    originalPrice: 299,
    duration: '30 days',
    perDay: '3.30',
    color: '#38bdf8',
    rgb: '56,189,248',
    icon: Clock,
    description: 'Try the full suite risk-free for a month.',
    features: [
      'All KiteKraken AI indicators',
      'Real-time signals & alerts',
      'VIP Telegram community',
      'Video tutorials & courses',
      '24/7 support',
    ],
    popular: false,
  },
  {
    id: 2,
    name: '180-Day Access',
    price: 499,
    originalPrice: 799,
    duration: '6 months',
    perDay: '2.77',
    color: '#38FE60',
    rgb: '56,254,96',
    icon: Zap,
    description: 'Our most popular plan for committed traders.',
    features: [
      'All KiteKraken AI indicators',
      'Real-time signals & alerts',
      'VIP Telegram community',
      'Video tutorials & courses',
      '24/7 support',
      'Priority support',
    ],
    popular: true,
    badge: 'Most Popular',
  },
  {
    id: 3,
    name: '365-Day Access',
    price: 999,
    originalPrice: 1499,
    duration: '1 year',
    perDay: '2.74',
    color: '#a78bfa',
    rgb: '167,139,250',
    icon: Crown,
    description: 'Maximum value for serious long-term traders.',
    features: [
      'All KiteKraken AI indicators',
      'Real-time signals & alerts',
      'VIP Telegram community',
      'Video tutorials & courses',
      '24/7 support',
      'Priority support',
      'Exclusive webinars',
    ],
    popular: false,
    badge: 'Best Value',
  },
];

const ALL_FEATURES = [
  { icon: BarChart3, label: 'KiteKraken AI Premium Suite (all indicators)' },
  { icon: Zap,       label: 'Real-time signals & alerts' },
  { icon: BarChart3, label: 'Candle coloring & dashboard analytics' },
  { icon: MessageCircle, label: 'VIP Telegram access with 30+ expert analysts' },
  { icon: Users,     label: 'Thousands of live alerts across every market' },
  { icon: Play,      label: 'Video tutorials & courses' },
  { icon: Shield,    label: 'Support & resistance levels' },
  { icon: Star,      label: 'Institutional activity insights' },
  { icon: Clock,     label: 'Automated alerts & custom settings' },
  { icon: Shield,    label: '24/7 dedicated support' },
];

const FAQS = [
  { q: 'Can I cancel my subscription at any time?', a: 'Yes — cancel anytime with no penalties. Your access remains active until the end of your billing period.' },
  { q: 'What payment methods do you accept?', a: 'We accept all major credit/debit cards, PayPal, and select cryptocurrencies — all processed securely via Stripe with PCI compliance.' },
  { q: 'Is there a free trial available?', a: 'We don\'t offer a free trial, but our 30-Day plan lets you experience the full platform at the lowest commitment. Not satisfied? We\'ll work with you.' },
  { q: 'Do I need TradingView to use KiteKraken?', a: 'TradingView is required for our indicators and alert system. A free TradingView account works, but Pro unlocks more simultaneous alerts.' },
  { q: 'What\'s included in VIP Telegram access?', a: 'Our private Telegram group has 30+ professional analysts posting real-time trade ideas, market commentary, and strategy sessions daily.' },
  { q: 'Can I upgrade my plan mid-term?', a: 'Absolutely. Upgrade anytime and we\'ll credit your unused days toward the new plan — no double charging.' },
];

const TRUST = [
  { icon: Lock,       label: 'PCI Compliant' },
  { icon: Shield,     label: 'SSL Secured' },
  { icon: RefreshCw,  label: 'Cancel Anytime' },
  { icon: Pause,      label: 'Free Account Hold' },
  { icon: Users,      label: '66,000+ Traders' },
  { icon: Star,       label: '4.9 / 5 Rating' },
];

const PricingCard = ({ plan, index }) => {
  const navigate = useNavigate();
  const [hov, setHov] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [ref, vis] = useVisible(0.07);
  const Icon = plan.icon;

  const addRipple = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples(r => [...r, { x: e.clientX - rect.left, y: e.clientY - rect.top, id }]);
    setTimeout(() => setRipples(r => r.filter(rip => rip.id !== id)), 700);
  };

  const savings = plan.originalPrice - plan.price;
  const savePct = Math.round(savings / plan.originalPrice * 100);

  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? (plan.popular ? 'scale(1.03)' : 'none') : 'translateY(36px)',
      transition: `opacity 0.7s ${index * 100}ms ease, transform 0.7s ${index * 100}ms ease`,
      position: 'relative', flex: '1 1 280px', maxWidth: 380,
      display: 'flex', flexDirection: 'column',
    }}>
      {plan.badge && (
        <div style={{
          position: 'absolute', top: -14, left: '50%',
          transform: 'translateX(-50%)',
          background: plan.popular ? '#38FE60' : `rgba(${plan.rgb},0.15)`,
          color: plan.popular ? '#000' : plan.color,
          border: plan.popular ? 'none' : `1px solid rgba(${plan.rgb},0.4)`,
          padding: '4px 14px', borderRadius: 20,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
          whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
          animation: 'badgePop 0.5s ease forwards',
          zIndex: 2,
        }}>
          {plan.popular ? <Zap style={{ width: 11, height: 11 }} /> : <Crown style={{ width: 11, height: 11 }} />}
          {plan.badge}
        </div>
      )}

      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        onClick={addRipple}
        style={{
          borderRadius: 20,
          padding: '36px 28px 28px',
          background: plan.popular
            ? `linear-gradient(160deg, rgba(${plan.rgb},0.07) 0%, rgba(255,255,255,0.025) 100%)`
            : 'rgba(255,255,255,0.03)',
          border: `1px solid ${hov || plan.popular ? `rgba(${plan.rgb},0.45)` : 'rgba(255,255,255,0.08)'}`,
          boxShadow: plan.popular
            ? `0 0 40px rgba(${plan.rgb},0.12), inset 0 1px 0 rgba(255,255,255,0.05)`
            : hov ? `0 0 28px rgba(${plan.rgb},0.1)` : 'none',
          transform: hov ? 'translateY(-6px)' : 'none',
          transition: 'border 0.25s, box-shadow 0.25s, transform 0.25s',
          position: 'relative', overflow: 'hidden', cursor: 'pointer',
          animation: plan.popular ? 'borderGlow 3.5s ease-in-out infinite' : 'none',
          display: 'flex', flexDirection: 'column', flex: 1,
        }}
      >
        {ripples.map(r => (
          <span key={r.id} style={{
            position: 'absolute', left: r.x, top: r.y,
            width: 10, height: 10, borderRadius: '50%',
            background: `rgba(${plan.rgb},0.35)`,
            transform: 'scale(0)', pointerEvents: 'none',
            animation: 'rippleOut 0.7s ease-out forwards',
            marginLeft: -5, marginTop: -5,
          }} />
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: `rgba(${plan.rgb},0.12)`,
              border: `1px solid rgba(${plan.rgb},0.2)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 14,
            }}>
              <Icon style={{ width: 20, height: 20, color: plan.color }} />
            </div>
            <h3 style={{ color: '#fff', fontSize: 19, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 5 }}>{plan.name}</h3>
            <p style={{ color: '#aaa', fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>{plan.description}</p>
          </div>
          <div style={{
            background: `rgba(${plan.rgb},0.12)`,
            border: `1px solid rgba(${plan.rgb},0.22)`,
            borderRadius: 8, padding: '4px 9px', textAlign: 'center',
          }}>
            <div style={{ color: plan.color, fontSize: 13, fontWeight: 800 }}>{savePct}%</div>
            <div style={{ color: '#666', fontSize: 9, fontWeight: 600, letterSpacing: '0.06em' }}>OFF</div>
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <span style={{ color: '#666', fontSize: 14, textDecoration: 'line-through', fontWeight: 600 }}>${plan.originalPrice}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
          <span style={{ color: plan.popular ? '#38FE60' : plan.color, fontSize: 'clamp(2.8rem,5vw,3.6rem)', fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1 }}>
            ${plan.price}
          </span>
          <span style={{ color: '#777', fontSize: 13, fontWeight: 600 }}>/ {plan.duration}</span>
        </div>
        <div style={{ color: '#888', fontSize: 12, fontWeight: 600, marginBottom: 24 }}>
          ≈ <span style={{ color: plan.color, fontWeight: 700 }}>${plan.perDay}</span> / day
        </div>

        <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 20 }} />

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
          {plan.features.map((f, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%',
                background: `rgba(${plan.rgb},0.15)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 1,
              }}>
                <Check style={{ width: 10, height: 10, color: plan.color }} />
              </div>
              <span style={{ color: '#ddd', fontSize: 13, fontWeight: 600, lineHeight: 1.6 }}>{f}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={(e) => { e.stopPropagation(); navigate('/contact'); }}
          style={{
            width: '100%', padding: '13px 0',
            background: plan.popular ? '#38FE60' : `rgba(${plan.rgb},0.1)`,
            color: plan.popular ? '#000' : plan.color,
            border: plan.popular ? 'none' : `1px solid rgba(${plan.rgb},0.35)`,
            borderRadius: 12, fontSize: 13, fontWeight: 700,
            fontFamily: "'Space Grotesk', sans-serif",
            cursor: 'pointer', letterSpacing: '0.01em',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            boxShadow: plan.popular ? '0 0 28px rgba(56,254,96,0.3)' : 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = `0 0 32px rgba(${plan.rgb},0.4)`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = plan.popular ? '0 0 28px rgba(56,254,96,0.3)' : 'none';
          }}
        >
          Get Started <ArrowRight style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </div>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const [ref, vis] = useVisible(0.07);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : 'translateY(18px)',
      transition: `opacity 0.6s ${index * 60}ms ease, transform 0.6s ${index * 60}ms ease`,
      borderRadius: 14,
      border: `1px solid ${open ? 'rgba(56,254,96,0.22)' : 'rgba(255,255,255,0.07)'}`,
      background: open ? 'rgba(56,254,96,0.03)' : 'rgba(255,255,255,0.025)',
      overflow: 'hidden',
      transition: 'border 0.2s, background 0.2s',
      cursor: 'pointer',
    }}
      onClick={() => setOpen(o => !o)}
    >
      <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ color: '#fff', fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>{item.q}</span>
        <ChevronDown style={{
          width: 18, height: 18, color: open ? '#38FE60' : '#555', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.3s, color 0.3s',
        }} />
      </div>
      <div style={{
        maxHeight: open ? 200 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.38s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <p style={{ color: '#aaa', fontSize: 13, lineHeight: 1.8, padding: '0 22px 18px' }}>{item.a}</p>
      </div>
    </div>
  );
};

const Pricing = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#111113', minHeight: '100vh', fontFamily: "'Space Grotesk', sans-serif", color: '#fff' }}>
      <style>{KEYFRAMES}</style>

      <section style={{ padding: '100px 24px 72px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 700, height: 400, background: 'radial-gradient(ellipse, rgba(56,254,96,0.06) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 40, right: '10%', width: 280, height: 280, background: 'radial-gradient(circle, rgba(56,189,248,0.04) 0%, transparent 65%)', pointerEvents: 'none', animation: 'floatA 9s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: 0, left: '8%', width: 200, height: 200, background: 'radial-gradient(circle, rgba(167,139,250,0.04) 0%, transparent 65%)', pointerEvents: 'none', animation: 'floatB 11s ease-in-out infinite' }} />

        <FadeIn>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(56,254,96,0.08)', border: '1px solid rgba(56,254,96,0.18)', borderRadius: 20, padding: '6px 14px', marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 8px #38FE60', display: 'inline-block' }} className="animate-pulse" />
            <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Simple Pricing</span>
          </div>
        </FadeIn>

        <FadeIn delay={60}>
          <h1 style={{ fontSize: 'clamp(2.8rem,7vw,5rem)', fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1.05, marginBottom: 20, maxWidth: 680, margin: '0 auto 20px' }}>
            Choose Your<br />
            <span style={{ color: '#38FE60', textShadow: '0 0 50px rgba(56,254,96,0.25)' }}>Access Plan</span>
          </h1>
        </FadeIn>

        <FadeIn delay={120}>
          <p style={{ color: '#999', fontSize: 17, lineHeight: 1.7, maxWidth: 520, margin: '0 auto 12px' }}>
            All plans include the complete KiteKraken AI suite, VIP Telegram community, and lifetime access to updates.
          </p>
        </FadeIn>

        <FadeIn delay={160}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
            {[
              { icon: Shield, label: 'Secure Checkout' },
              { icon: RefreshCw, label: 'Cancel Anytime' },
              { icon: Lock,   label: 'No Hidden Fees' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#777', fontSize: 12 }}>
                <Icon style={{ width: 13, height: 13, color: '#38FE60' }} />
                {label}
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch', paddingTop: 20 }}>
            {PLANS.map((plan, i) => (
              <PricingCard key={plan.id} plan={plan} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} />
                <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Everything Included</span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 10 }}>
                What's Included With Every Plan
              </h2>
              <p style={{ color: '#888', fontSize: 15 }}>No tiers, no gating — every plan unlocks the full platform.</p>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {ALL_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <FadeIn key={f.label} delay={i * 40}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 18px', borderRadius: 12,
                      background: 'rgba(255,255,255,0.028)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      transition: 'border 0.2s, background 0.2s, transform 0.2s',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.border = '1px solid rgba(56,254,96,0.25)';
                      e.currentTarget.style.background = 'rgba(56,254,96,0.03)';
                      e.currentTarget.style.transform = 'translateX(3px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.border = '1px solid rgba(255,255,255,0.07)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.028)';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(56,254,96,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon style={{ width: 14, height: 14, color: '#38FE60' }} />
                    </div>
                    <span style={{ color: '#ccc', fontSize: 13, lineHeight: 1.5 }}>{f.label}</span>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} />
                <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>FAQ</span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 800, letterSpacing: '-0.04em' }}>
                Frequently Asked Questions
              </h2>
            </div>
          </FadeIn>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FAQS.map((item, i) => (
              <FaqItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn>
            <div style={{
              borderRadius: 20, padding: '32px 40px',
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, justifyContent: 'center' }}>
                <span style={{ color: '#777', fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Trusted & Secure</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
                {TRUST.map(({ icon: Icon, label }, i) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 18px', borderRadius: 30,
                      background: 'rgba(255,255,255,0.035)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontSize: 12, color: '#bbb', fontWeight: 500,
                      transition: 'border 0.2s, color 0.2s',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(56,254,96,0.3)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#bbb'; }}
                  >
                    <Icon style={{ width: 13, height: 13, color: '#38FE60' }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section style={{ padding: '0 24px 100px' }}>
        <FadeIn>
          <div style={{
            maxWidth: 820, margin: '0 auto',
            borderRadius: 24, padding: '64px 40px',
            background: 'linear-gradient(145deg, rgba(56,254,96,0.06) 0%, rgba(255,255,255,0.02) 60%, rgba(56,189,248,0.03) 100%)',
            border: '1px solid rgba(56,254,96,0.15)',
            textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', width: 500, height: 300, background: 'radial-gradient(ellipse, rgba(56,254,96,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(56,254,96,0.1)', border: '1px solid rgba(56,254,96,0.2)', borderRadius: 20, padding: '5px 13px', marginBottom: 20 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#38FE60', display: 'inline-block' }} className="animate-pulse" />
              <span style={{ color: '#38FE60', fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Get Started Today</span>
            </div>
            <h2 style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1, marginBottom: 14 }}>
              Ready to Trade Smarter?
            </h2>
            <p style={{ color: '#999', fontSize: 16, lineHeight: 1.7, maxWidth: 480, margin: '0 auto 36px' }}>
              Join 66,000+ traders using KiteKraken AI to automate and scale their strategies.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{
                  padding: '14px 36px', background: '#38FE60', color: '#000',
                  border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 700,
                  fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 7,
                  boxShadow: '0 0 28px rgba(56,254,96,0.3)',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 50px rgba(56,254,96,0.55)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 28px rgba(56,254,96,0.3)'; e.currentTarget.style.transform = 'none'; }}
              >
                Choose a Plan <ArrowRight style={{ width: 15, height: 15 }} />
              </button>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  padding: '14px 28px', background: 'transparent', color: '#ccc',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12,
                  fontSize: 13, fontWeight: 600,
                  fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer',
                  transition: 'border 0.2s, color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.35)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#ccc'; }}
              >
                Talk to Sales
              </button>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default Pricing;
